import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import http from '../../services/http';
import { mensagemDoErro, statusDoErro } from '../../utils/numeroDeWhatsapp';

/**
 * O gasto do mês no WhatsApp, número por número.
 *
 * ## Por que esta tela existe
 *
 * Desde 01/10/2026 a Meta dá 1.000 mensagens de serviço grátis por NÚMERO por
 * mês, e cobra cada uma que passar disso. Ou seja: a conta não é da empresa, é
 * de cada número separado -- e o estouro de um não aparece no total do outro.
 * Sem esta tela não há como saber onde a franquia está antes da fatura chegar.
 *
 * ## O que ela não mostra, de propósito
 *
 * Reais. A Meta informa, no webhook, SE cobra e de que categoria -- não
 * quanto. Estimar o valor exigiria a tabela de tarifas dela por país e
 * categoria, que muda sem aviso. Um número estimado numa tela de custo é lido
 * como fatura, e errar para baixo é pior que não mostrar nada. O aviso no
 * rodapé diz isso para quem abrir procurando o valor.
 *
 * ## Componente separado da página
 *
 * `WhatsAppPage` já tem mais de mil linhas, e o relatório carrega os próprios
 * dados por um endpoint próprio. Juntar faria uma falha do relatório derrubar
 * o cadastro -- que é a tela que alguém abre justamente para consertar um
 * número que não está funcionando.
 */

/** Quantos meses para trás o seletor oferece. */
const MESES_NO_SELETOR = 12;

/**
 * Os rótulos das categorias da Meta, em português.
 *
 * As chaves são os valores que ela manda (`service`, `utility`,
 * `marketing`, `authentication`) e não são traduzidas no banco de propósito --
 * a tradução mora aqui, onde é texto de tela.
 */
const CATEGORIAS = {
  service: 'Atendimento',
  utility: 'Utilidade',
  marketing: 'Marketing',
  authentication: 'Autenticação',
};

const dePara = (mes) => {
  const [ano, numero] = String(mes).split('-');
  const data = new Date(Number(ano), Number(numero) - 1, 1);
  const texto = data.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
  return texto.charAt(0).toUpperCase() + texto.slice(1);
};

const mesesDisponiveis = () => {
  const hoje = new Date();
  const lista = [];
  for (let i = 0; i < MESES_NO_SELETOR; i += 1) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    const mes = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    lista.push({ valor: mes, rotulo: dePara(mes) });
  }
  return lista;
};

/**
 * A cor da barra da franquia.
 *
 * Três faixas, e o aviso chega ANTES do estouro: avisar só quando já estourou
 * não serve para nada -- o dinheiro já foi gasto. 80% de 1.000 ainda dá tempo
 * de decidir se vale segurar envio no fim do mês.
 *
 * As cores saem da paleta do MUI (`success`/`warning`/`error`), que já tem
 * tom próprio para tema claro e escuro. Hexadecimal chapado aqui ficaria
 * ilegível num dos dois.
 */
export const corDaFranquia = (consumido, total) => {
  if (!total || consumido >= total) return 'error';
  if (consumido / total >= 0.8) return 'warning';
  return 'success';
};

const GastoDoWhatsApp = () => {
  const meses = useMemo(() => mesesDisponiveis(), []);
  const [mes, setMes] = useState(meses[0].valor);
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [semPermissao, setSemPermissao] = useState(false);

  const carregar = useCallback(async (mesPedido) => {
    setCarregando(true);
    setErro('');
    setSemPermissao(false);
    try {
      const resposta = await http.get('/whatsapp-numbers/gastos', { params: { mes: mesPedido } });
      setDados(resposta?.data?.data ?? null);
    } catch (e) {
      if (statusDoErro(e) === 403) {
        setSemPermissao(true);
      } else {
        setErro(mensagemDoErro(e, 'Não foi possível carregar o gasto do WhatsApp.'));
      }
      setDados(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar(mes);
  }, [carregar, mes]);

  const numeros = Array.isArray(dados?.numeros) ? dados.numeros : [];
  const franquia = Number(dados?.franquia_de_servico) || 0;

  if (semPermissao) {
    // A página inteira já mostra o bloco de "acesso não liberado" quando é
    // isso. Repetir aqui daria duas mensagens iguais na mesma tela.
    return null;
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="gasto-do-whatsapp">
      <Box sx={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1,
      }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Gasto por número
          </Typography>
          <Typography variant="caption" color="text.secondary">
            A franquia de mensagens de atendimento da Meta é de {franquia || 1000} por número, por mês.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            select
            size="small"
            label="Mês"
            value={mes}
            onChange={(e) => setMes(e.target.value)}
            sx={{ minWidth: 180 }}
            inputProps={{ 'data-testid': 'mes-do-gasto' }}
          >
            {meses.map((m) => (
              <MenuItem key={m.valor} value={m.valor}>{m.rotulo}</MenuItem>
            ))}
          </TextField>
          <Tooltip title="Atualizar">
            <IconButton size="small" onClick={() => carregar(mes)} aria-label="Atualizar gasto">
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {carregando && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {/* Os três estados que a tela desenha parecidos e que significam coisas
          opostas: "não consegui carregar", "não há número cadastrado" e "há
          número e ele não gastou nada". Trocar um pelo outro faria alguém
          procurar um problema que não existe -- ou deixar de procurar o que
          existe. */}
      {!carregando && erro && (
        <Alert severity="error" sx={{ mt: 1 }}>{erro}</Alert>
      )}

      {!carregando && !erro && numeros.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          Nenhum número de WhatsApp cadastrado.
        </Typography>
      )}

      {!carregando && !erro && numeros.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
          {numeros.map((n) => {
            const consumido = Number(n.na_franquia) || 0;
            const cor = corDaFranquia(consumido, franquia);
            const porcento = franquia > 0 ? Math.min(100, (consumido / franquia) * 100) : 0;

            return (
              <Box
                key={n.whatsapp_number_id}
                data-testid={`gasto-numero-${n.whatsapp_number_id}`}
                sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}
              >
                <Box sx={{
                  display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap',
                }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {n.rotulo}
                      {!n.ativo && (
                        <Chip label="Inativo" size="small" sx={{ ml: 1 }} />
                      )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {n.display_number}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      {n.entregues} entregue{n.entregues === 1 ? '' : 's'}
                    </Typography>
                    <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
                      {n.cobradas} cobrada{n.cobradas === 1 ? '' : 's'}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ mt: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                    <Typography variant="caption" color="text.secondary">
                      Franquia de atendimento
                    </Typography>
                    {/* O número em `text.primary`, e NÃO na cor do estado.
                        Medido: `success.main` sobre o papel do tema claro dá
                        3,30:1 -- abaixo dos 4,5:1 de texto pequeno. A cor fica
                        na barra, que é gráfico grande (3:1 basta), e o estado
                        é dito com palavras logo abaixo. */}
                    <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
                      {consumido} de {franquia}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={porcento}
                    color={cor}
                    sx={{ height: 6, borderRadius: 3 }}
                    aria-label={`Franquia do número ${n.rotulo}`}
                  />
                </Box>

                {/* O estado DITO, e não só colorido. Quem não distingue verde
                    de vermelho veria o mesmo cartão -- e é esta a informação
                    que custa dinheiro. O aviso de "perto do limite" existe
                    porque avisar só no estouro não serve para nada: o dinheiro
                    já foi gasto. */}
                {n.estourou_a_franquia && (
                  <Alert severity="error" icon={<ErrorOutlineIcon fontSize="inherit" />} sx={{ mt: 1, py: 0 }}>
                    A franquia deste número acabou. As mensagens de atendimento daqui para a frente
                    são cobradas.
                  </Alert>
                )}

                {!n.estourou_a_franquia && cor === 'warning' && (
                  <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                    Perto do limite: restam {n.franquia_restante} mensagens de atendimento grátis
                    neste número.
                  </Alert>
                )}

                <Box sx={{ display: 'flex', gap: 0.5, mt: 1, flexWrap: 'wrap' }}>
                  {Object.keys(CATEGORIAS).map((chave) => {
                    const quantas = Number(n.por_categoria?.[chave]) || 0;
                    if (quantas === 0) return null;
                    return (
                      <Chip
                        key={chave}
                        size="small"
                        variant="outlined"
                        label={`${CATEGORIAS[chave]}: ${quantas}`}
                      />
                    );
                  })}
                </Box>

                {/* As duas colunas que denunciam problema em vez de gasto.
                    Escondê-las deixaria a soma parecendo fechada: a primeira é
                    webhook que não chegou, a segunda é fila travada. */}
                {(n.sem_preco_informado > 0 || n.enviadas_sem_entrega > 0) && (
                  <Box sx={{ display: 'flex', gap: 1.5, mt: 1, flexWrap: 'wrap' }}>
                    {n.sem_preco_informado > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {n.sem_preco_informado} sem preço informado pela Meta
                      </Typography>
                    )}
                    {n.enviadas_sem_entrega > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {n.enviadas_sem_entrega} enviada{n.enviadas_sem_entrega === 1 ? '' : 's'} sem confirmação de entrega
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      )}

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mt: 1.5 }}>
        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', mt: '2px' }} />
        <Typography variant="caption" color="text.secondary">
          A contagem é de mensagens ENTREGUES, que é o que a Meta cobra. O valor em reais não
          aparece aqui porque a Meta informa que cobra, não quanto — a tarifa muda por país e
          categoria, e o valor oficial é o da fatura dela.
        </Typography>
      </Box>
    </Paper>
  );
};

export default GastoDoWhatsApp;
